package ro.horatiu_udrea.resursedeblocat

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.KeyboardArrowRight
import androidx.compose.material3.*
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import org.jetbrains.compose.resources.stringResource
import resursedeblocat.composeapp.generated.resources.*

@Composable
fun TrackingScreen(
    viewModel: TrackingViewModel,
    onAddTracking: (MonthYear) -> Unit,
    onTrackingClick: (MonthlyTracking) -> Unit,
    onLogout: () -> Unit
) {
    val scrollState = rememberScrollState()

    LaunchedEffect(Unit) {
        viewModel.loadTrackings()
    }

    BaseScreenLayout(
        scrollState = scrollState,
        footer = {
            Spacer(modifier = Modifier.height(16.dp))
            TextButton(
                onClick = onLogout,
                modifier = Modifier.padding(bottom = 8.dp)
            ) {
                Text(
                    text = stringResource(Res.string.logout),
                    style = MaterialTheme.typography.labelLarge,
                )
            }
        }
    ) {
        BoxWithConstraints(modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp)) {
            val isWide = maxWidth > 500.dp
            val canTrack = viewModel.canTrackLastMonth()
            
            val content = @Composable { modifier: Modifier ->
                Column(
                    modifier = modifier.padding(horizontal = 12.dp),
                    horizontalAlignment = if (isWide) Alignment.Start else Alignment.CenterHorizontally
                ) {
                    Text(
                        text = stringResource(Res.string.tab_tracking),
                        style = MaterialTheme.typography.headlineMedium,
                        color = MaterialTheme.colorScheme.secondary
                    )
                }
            }
            
            val button = @Composable { modifier: Modifier ->
                Button(
                    onClick = { 
                        if (canTrack) {
                            onAddTracking(viewModel.getLastMonthYear())
                        }
                    },
                    enabled = canTrack,
                    shape = MaterialTheme.shapes.medium,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = MaterialTheme.colorScheme.primary,
                        contentColor = MaterialTheme.colorScheme.onPrimary
                    ),
                    modifier = modifier
                ) {
                    Text(stringResource(Res.string.track_last_month), style = MaterialTheme.typography.labelLarge)
                }
            }

            if (isWide) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    content(Modifier)
                    button(Modifier)
                }
            } else {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(8.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    content(Modifier.fillMaxWidth())
                    button(Modifier.fillMaxWidth())
                }
            }
        }

        if (viewModel.isLoading) {
            Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator()
            }
        } else if (viewModel.trackings.isEmpty()) {
            Box(modifier = Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                Text(
                    text = stringResource(Res.string.no_trackings),
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        } else {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                viewModel.trackings.forEach { tracking ->
                    TrackingItem(
                        tracking = tracking,
                        onClick = { onTrackingClick(tracking) }
                    )
                }
            }
        }
        
        viewModel.errorMessage?.let { error ->
            Text(
                text = error,
                color = MaterialTheme.colorScheme.error,
                modifier = Modifier.padding(top = 8.dp)
            )
        }
    }
}

@Composable
fun TrackingItem(
    tracking: MonthlyTracking,
    onClick: () -> Unit
) {
    val monthRes = when (tracking.month) {
        1 -> Res.string.month_1
        2 -> Res.string.month_2
        3 -> Res.string.month_3
        4 -> Res.string.month_4
        5 -> Res.string.month_5
        6 -> Res.string.month_6
        7 -> Res.string.month_7
        8 -> Res.string.month_8
        9 -> Res.string.month_9
        10 -> Res.string.month_10
        11 -> Res.string.month_11
        12 -> Res.string.month_12
        else -> Res.string.month_1
    }
    
    Card(
        onClick = onClick,
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .clickable(onClick = onClick),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        shape = RoundedCornerShape(12.dp),
    ) {
        Row(
            modifier = Modifier.padding(16.dp).fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.SpaceBetween
        ) {
            Column {
                Text(
                    text = stringResource(Res.string.monthly_tracking_title, stringResource(monthRes), tracking.year),
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold
                )
                if (tracking.completed) {
                    Text(
                        text = stringResource(Res.string.state_finished),
                        style = MaterialTheme.typography.bodySmall,
                        color = Color(0xFF4CAF50) // Green
                    )
                } else {
                    Text(
                        text = stringResource(Res.string.state_in_progress),
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.primary
                    )
                }
            }
            Icon(
                imageVector = Icons.AutoMirrored.Filled.KeyboardArrowRight,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary
            )
        }
    }
}
